import { logError } from '../../../src/utils/logger';

export const fileExtToMIMETypes = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.ttf': 'font/ttf',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
  '.otf': 'font/otf'
};

export function isDataURL (s) {
  if (!s) return false;
  return !!s.match(/^\s*data:(\w+\/[-.\w]+(?:\+[-.\w]+)?)?(;base64)?,[a-z0-9!$&',()*+;=\-._~:@/?%\s]*\s*$/i);
}

export function isMimeType (s) {
  if (!s) return false;
  return !!s.match(/(\w+\/[-.\w]+(?:\+[-.\w]+)?)/i);
}

const createLocalStorageKey = (url) => `dataURLCache-${Base64.encode(url)}`;
const setCacheItem = (url, dataURL, retries = 3) => {
  try {
    localStorage.setItem(createLocalStorageKey(url), dataURL);
  } catch (e) {
    INKIVE.C.DEBUG && console.warn('Local storage full. Clearing storage...', e);
    localStorage.clear();
  }
};

export async function getDataURL (url, doNotCache = true) {
  if (!doNotCache) {
    const cachedDataURL = localStorage.getItem(createLocalStorageKey(url));
    if (isDataURL(cachedDataURL)) return cachedDataURL;
  }

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const dataURL = await blobToBase64(blob, url);

    if (!doNotCache && isDataURL(dataURL)) {
      setCacheItem(url, dataURL);
    }

    return dataURL;
  } catch (error) {
    logError('getDataURL')(error);
  }
}

export function sniffMimeType (blob) {
  /* Some images don't specify a Content-Type header
  * so the blob.type is coming as binary/octet-stream or application/octet-stream
  * I fix that by trying to guess the mime-type
  */

  const getMimeTypeFromHex = (signature) => {
    // https://mimesniff.spec.whatwg.org/#matching-an-image-type-pattern
    switch (signature) {
      case '89504E47':
        return 'image/png';
      case '47494638':
        return 'image/gif';
      case '25504446':
        return 'application/pdf';
      case '504B0304':
        return 'application/zip';
      case 'FFD8FFDB':
      case 'FFD8FFE0':
      case 'FFD8FFE1':
      case 'FFD8FFE2':
      case 'FFD8FFE3':
      case 'FFD8FFE8':
      case '52494646': // webp
      case '0000':
        return 'image/jpeg';
      default:
        return null; // Unknown
    }
  };

  return new Promise((resolve, reject) => {
    const blobSignature = blob.slice(0, 4);
    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (e.target.readyState === FileReader.DONE) {
        const uint = new Uint8Array(e.target.result);
        const bytes = uint.map((byte) => byte.toString(16));
        const hex = bytes.join('').toUpperCase();
        const MIMEType = getMimeTypeFromHex(hex);
        if (MIMEType) {
          resolve(MIMEType);
        } else {
          reject(hex);
        }
      }
    };
    reader.readAsArrayBuffer(blobSignature);
  });
}

export async function getMIMEType (url, blob) {
  // Don't trust blob.type!
  // Some images have content-type: width="1920" height="1080"
  const mimeType = getMimeTypeFromUrl(url);
  if (mimeType && isMimeType(mimeType)) {
    return mimeType;
  }

  if (blob?.type && isMimeType(blob.type)) {
    return blob.type;
  }

  if (blob) {
    const mimeType = await sniffMimeType(blob);
    if (mimeType && isMimeType(mimeType)) return mimeType;
  }

  if (url) {
    const mimeType = await getMimeTypeFromRequestUrl(url);

    if (mimeType && isMimeType(mimeType)) return mimeType;
  }

  return null;
}

export function getMimeTypeFromUrl (url) {
  if (!url || typeof url !== 'string') return null;

  return Object.entries(fileExtToMIMETypes).reduce((result, [format, mimeType]) => {
    return !result && url.includes(format)
      ? mimeType
      : result;
  }, null);
}

export function getMimeTypeFromDataUrl (dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return null;

  return dataUrl.split(';')[0].split(':')[1] || null;
}

export async function getMimeTypeFromContentType (contentType) {
  if (!contentType || typeof contentType !== 'string') return null;

  return Object.values(fileExtToMIMETypes)
    .find(mimeType => contentType.includes(mimeType)) || null;
}

export async function getMimeTypeFromRequestUrl (url) {
  try {
    const request = await fetch(url, { method: 'HEAD' });

    return getMimeTypeFromContentType(request.headers.get('Content-Type')) ||
      getMimeTypeFromContentType(request.headers.get('content-type'));
  } catch (error) {
    return null;
  }
}

async function blobToBase64 (blob, url) {
  let MIMEType;
  try {
    MIMEType = await getMIMEType(url, blob);
  } catch (hexSignature) {
    throw constructError({ blob, url, hexSignature });
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split('base64,').pop();
      const dataURL = `data:${MIMEType};base64,${base64}`;
      if (isDataURL(dataURL)) {
        resolve(dataURL);
      } else {
        reject(constructError({ blob, url, dataURL }));
      }
    };
    reader.readAsDataURL(blob);
  });
}

function constructError ({ blob, url, hexSignature, dataURL }) {
  const message = `Data url could not be generated.
Blob size: ${blob.size}
Blob hex signature: ${hexSignature}
Original url: ${url}
Failed dataURL: ${dataURL}`;
  return new Error(message);
}

export function getQueryParameter (name) {
  let hash;
  let hashes = window.location.href;
  hashes = hashes.slice(hashes.indexOf('?') + 1).split('&');
  for (let i = 0; i < hashes.length; i++) {
    hash = hashes[i].split('=');
    if (hash[0] === name) {
      return sanitizeQueryParameter(hash[1]);
    }
  }
}

function sanitizeQueryParameter(hashParam) {
  let param = decodeURIComponent(hashParam);

  if (
    param.includes('__proto__') ||
    param.includes('constructor.prototype') ||
    param.includes('javascript')
  ) {
    param = '';
  }

  return param;
}
